import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const REPORT_NAME_MAX_LENGTH = 100;
export const REPORT_QUERY_MAX_LENGTH = 8000;

export type FirmReportChoice =
  | { listName: string; status: "ready" | "loading" }
  | { listName: string; status: "failed"; onRetry: () => void };

export const ReportNameModal = ({
  title,
  description,
  confirmLabel,
  initialName = "",
  isLoading,
  confirmDisabledReason,
  firmReportChoice,
  onConfirm,
  onClose,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  initialName?: string;
  isLoading: boolean;
  confirmDisabledReason?: string;
  firmReportChoice?: FirmReportChoice;
  onConfirm: (name: string, firmReport: boolean) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [firmReport, setFirmReport] = useState(false);
  const trimmed = name.trim();
  const tooLong = trimmed.length > REPORT_NAME_MAX_LENGTH;
  const canConfirm = trimmed.length > 0 && !tooLong && !confirmDisabledReason;

  return (
    <Modal isOpen onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canConfirm) onConfirm(trimmed, firmReport);
        }}
      >
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {description && (
            <Text fontSize="sm" color="smMuted" mb="4">
              {description}
            </Text>
          )}
          <FormControl isInvalid={tooLong} isRequired>
            <FormLabel>{t("Name")}</FormLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <FormErrorMessage>
              {t("The name can be at most {{max}} characters.", { max: REPORT_NAME_MAX_LENGTH })}
            </FormErrorMessage>
          </FormControl>
          {firmReportChoice?.status === "failed" && (
            <Flex mt="4" gap="2" align="center" wrap="wrap" data-testid="firm-report-choice-failed">
              <Text fontSize="sm" color="smMuted">
                {t("Who sees the report can't be chosen right now. Saving keeps it for you only.")}
              </Text>
              <Button size="sm" variant="link" colorScheme="teal" onClick={firmReportChoice.onRetry}>
                {t("Try again")}
              </Button>
            </Flex>
          )}
          {firmReportChoice && firmReportChoice.status !== "failed" && (
            <FormControl as="fieldset" mt="4" isDisabled={firmReportChoice.status === "loading"}>
              <FormLabel as="legend">{t("Who sees the report?")}</FormLabel>
              <RadioGroup
                value={firmReport ? "organization" : "me"}
                onChange={(value) => setFirmReport(value === "organization")}
                isDisabled={firmReportChoice.status === "loading"}
              >
                <Stack spacing="2">
                  <Radio value="me">{t("Only me")}</Radio>
                  <Box>
                    <Radio value="organization">{t("The whole organisation (firm report)")}</Radio>
                    <Text fontSize="sm" color="smMuted" ml="6">
                      {t(
                        "Everyone finds it in the report picker for {{list}}, with their own access. Only administrators can change or delete it.",
                        { list: firmReportChoice.listName }
                      )}
                    </Text>
                  </Box>
                </Stack>
              </RadioGroup>
            </FormControl>
          )}
          {confirmDisabledReason && (
            <Text fontSize="sm" color="smMuted" mt="3" data-testid="report-name-confirm-disabled-reason">
              {confirmDisabledReason}
            </Text>
          )}
        </ModalBody>
        <ModalFooter gap="2" justifyContent="flex-start" flexDirection="row-reverse">
          <Button type="submit" colorScheme="teal" isDisabled={!canConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
